import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton } from '@mui/material';
import { ImageSearch, PictureAsPdf, Rotate90DegreesCcw, Rotate90DegreesCw } from '@mui/icons-material';
import ViewPDFDialog from 'components/dialogs/ViewPDFDialog';
import DropPdfOrImage from 'components/dropZone/DropPdfOrImage';
import useCars from 'modules/cars/hooks/useCars';
import Api from 'utils/api';


type DocPreviewProps = {
    type: string,
    carId: number,
}

const DocPreview = ({carId, type}: DocPreviewProps) => {
    const {t} = useTranslation();
    const {getDocument, rotatePdf} = useCars();
    const [url, setUrl] = useState<string>();
    const [err, setErr] = useState(false);
    const [asImage, toggleAsImage] = useReducer(open => {
        const upd = !open;
        localStorage.setItem('docAsImage', upd ? '1' : '0');
        return upd;
    }, localStorage.getItem('docAsImage') === '1');
    const navigate = useNavigate();

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                navigate(-1);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [navigate]);

    useEffect(() => {
        setErr(false);
        getDocument(carId, type)
            .then(data => setUrl(data.url))
            .catch(() => setErr(true))
    }, [carId, type, getDocument]);

    if (!url) return null;

    if (err) {
        return (
            <DropPdfOrImage
                upload={file => Api.cars.uploadDocument(carId, type, file).then(() => setErr(false))}
                title={t('upload_title', {value: t(type)})}
            />
        );
    }

    const rotate = (direction: string) => rotatePdf(getPath(url), direction)
        .then(() => getDocument(carId, type))
        .then(res => setUrl(res.url));

    return (
        <>
            {asImage ? (
                <img
                    width="100%"
                    src={`${process.env.REACT_APP_API_HOST}/pdf/image?path=${getPath(url)}`}
                    alt="Document"
                />
                ) : (
                <ViewPDFDialog
                    url={url}
                    height={window.innerHeight - 150}
                />
            )}

            <Box textAlign="center">
                <IconButton
                    aria-label="rotate pdf left"
                    onClick={() => rotate('left')}
                >
                    <Rotate90DegreesCcw/>
                </IconButton>

                <IconButton
                    aria-label="rotate pdf right"
                    onClick={() => rotate('right')}
                    sx={{marginRight: 'auto'}}
                >
                    <Rotate90DegreesCw/>
                </IconButton>

                <IconButton
                    aria-label="switch image to pdf"
                    onClick={toggleAsImage}
                    sx={{marginRight: 'auto'}}
                >
                    {asImage ? <PictureAsPdf/> : <ImageSearch/>}
                </IconButton>
            </Box>
        </>
    );
};

const getPath = (url: string) => new URL(url).pathname.substring(1);

export default DocPreview;
